// Libraries
import { v4 as uuidv4 } from "uuid";
import clsx from "clsx";
import { Card, CardHeader, Checkbox, Divider, FormControlLabel, Grid, List, ListItemButton, ListItemIcon, Typography } from "@mui/material";

// Components
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

// Utils
import { intersection, not, union } from "../utils";

type TCustomListProps<T> = {
	title: string;
	items: T[];
	checked: T[];
	showItemBy: keyof T;
	setChecked: (value: T[]) => void;
};

export default function CustomList<T>(props: Readonly<TCustomListProps<T>>) {
	const { title, items, checked, showItemBy, setChecked } = props;

	const numberOfChecked = (items: readonly T[]) => intersection(checked, items).length;

	const handleToggle = (value: T) => () => {
		const currentIndex = checked.indexOf(value);
		const newChecked = [...checked];

		if (currentIndex === -1) {
			newChecked.push(value);
		} else {
			newChecked.splice(currentIndex, 1);
		}

		setChecked(newChecked);
	};

	const handleToggleAll = (items: readonly T[]) => () => {
		if (numberOfChecked(items) === items.length) {
			setChecked(not(checked, items));
		} else {
			setChecked(union(checked, items));
		}
	};

	return (
		<Grid item xs={12} sm={5}>
			<Typography className="!font-montserrat !text-[16px] !font-medium text-default text-center !mb-3">{title}</Typography>
			<Card className="!rounded-md border border-disabled !shadow-none">
				<CardHeader
					component="div"
					className={clsx({
						"[&_.MuiCardHeader-avatar]:!w-full [&_.MuiCardHeader-avatar]:!m-0": true,
						"[&_.MuiCardHeader-avatar>.MuiFormControlLabel-root]:!m-0 [&_.MuiCardHeader-avatar>.MuiFormControlLabel-root]:!p-0 [&_.MuiCardHeader-avatar>.MuiFormControlLabel-root]:!w-full":
							true,
						"hover:!bg-[#f5f5f5]": true,
						"!m-0 !mt-[12px] !py-[2px] !px-3": true,
					})}
					avatar={
						<FormControlLabel
							control={
								<Checkbox
									className="!p-0"
									onClick={handleToggleAll(items)}
									checked={numberOfChecked(items) === items.length && items.length !== 0}
									indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
									disabled={items.length === 0}
								/>
							}
							label={<Typography className="!font-sans !text-[14.5px] !text-default !ml-[10px]">Select all</Typography>}
						/>
					}
				/>
				<Divider className="!mx-3 !my-[6px]" />
				<List sx={{ height: 201, overflow: "auto", p: 0, mb: "12px" }} dense component="div" role="list">
					{items.map((value: T) => {
						const key = uuidv4();
						const labelId = `transfer-list-all-item-${key}-label`;

						return (
							<ListItemButton key={key} role="listitem" onClick={handleToggle(value)} className="!p-0 !px-3">
								<ListItemIcon className="!p-0 !min-w-0">
									<Checkbox
										className="!p-0"
										checked={checked.includes(value)}
										tabIndex={-1}
										disableRipple
										inputProps={{
											"aria-labelledby": labelId,
										}}
									/>
								</ListItemIcon>
								<CustomCell value={String(value[showItemBy])} hasTooltip classes="normal !ml-[6px] !my-[3px] " />
							</ListItemButton>
						);
					})}
				</List>
			</Card>
		</Grid>
	);
}
